// ABOUTME: The talent's interview card, the same behind the email link and in the tracker: the time and kind, and per
// ABOUTME: state what can be done (pick and confirm a time, propose others, Still on?, move, cancel, rebook, join).
// ABOUTME: On the job page of an application it is a section of that page: rows and text buttons, no card.
import { lazy, ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import { AppButton } from 'components/AppButton';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import {
  JobPageRow,
  JobPageSection
} from 'modules/jobListings/components/JobPageParts';
import { CLOCK_TICK_MS, StillOnAnswer } from '../constants';
import { interviewErrorKey } from '../errors';
import { formatClock, formatTimeRange } from '../format';
import { invalidProposalsOf, refusalWindowOf } from '../proposals';
import {
  interviewCardKey,
  useInterviewCardQuery,
  useInterviewCommand
} from '../query';
import { canJoinNow, hasStarted } from '../rules';
import {
  ChangeInterviewRequest,
  InterviewApi,
  InterviewAppointmentStatus,
  InterviewKind,
  InterviewStatus,
  InterviewTime
} from '../types';
import {
  InterviewReasonDialog,
  InterviewReasonDialogKind
} from './InterviewReasonDialog';
import { ProposeTimesForm } from './ProposeTimesForm';

/** Loaded on the first join only: daily-js is a large bundle most visits to the card never need. */
const InterviewCall = lazy(() =>
  import('./InterviewCall').then(module => ({ default: module.InterviewCall }))
);

const KIND_COPY: Record<InterviewKind, TranslationKey> = {
  [InterviewKind.VIDEO]: 'interviews.kind.VIDEO',
  [InterviewKind.OWN_LINK]: 'interviews.kind.OWN_LINK',
  [InterviewKind.PHONE]: 'interviews.kind.PHONE',
  [InterviewKind.ON_SITE]: 'interviews.kind.ON_SITE',
  [InterviewKind.TRIAL_SHIFT]: 'interviews.kind.TRIAL_SHIFT'
};

const STATUS_COPY: Record<InterviewStatus, TranslationKey> = {
  [InterviewStatus.OPEN]: 'interviews.status.OPEN',
  [InterviewStatus.SCHEDULED]: 'interviews.status.SCHEDULED',
  [InterviewStatus.COMPLETED]: 'interviews.status.COMPLETED',
  [InterviewStatus.NO_SHOW]: 'interviews.status.NO_SHOW',
  [InterviewStatus.CANCELLED]: 'interviews.status.CANCELLED',
  [InterviewStatus.EXPIRED]: 'interviews.status.EXPIRED'
};

/** The status in a word or two, for the status row of the section on the job page. */
const STATUS_SHORT_COPY: Record<InterviewStatus, TranslationKey> = {
  [InterviewStatus.OPEN]: 'interviews.statusShort.OPEN',
  [InterviewStatus.SCHEDULED]: 'interviews.statusShort.SCHEDULED',
  [InterviewStatus.COMPLETED]: 'interviews.statusShort.COMPLETED',
  [InterviewStatus.NO_SHOW]: 'interviews.statusShort.NO_SHOW',
  [InterviewStatus.CANCELLED]: 'interviews.statusShort.CANCELLED',
  [InterviewStatus.EXPIRED]: 'interviews.statusShort.EXPIRED'
};

const SECTION_HEADING_ID = 'interview-section-title';

/**
 * One of the card's actions: a button on the card, a text button in primary on the job page, like the page's links.
 */
const Action = ({
  asSection,
  testId,
  variant,
  color,
  disabled,
  onClick,
  children
}: {
  asSection: boolean;
  testId: string;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'error';
  disabled?: boolean;
  onClick: () => void;
  children: string;
}) =>
  asSection ? (
    <Link
      component="button"
      type="button"
      variant="body2"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      color={color === 'error' ? 'error.main' : 'primary.main'}
      sx={{
        fontWeight: 600,
        alignSelf: 'flex-start',
        textAlign: 'left',
        '&:disabled': { color: 'text.disabled', cursor: 'default' }
      }}
    >
      {children}
    </Link>
  ) : (
    <AppButton
      variant={variant}
      color={color}
      testId={testId}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </AppButton>
  );

/** pro-core cancels an OPEN, SCHEDULED or NO_SHOW interview; a finished, cancelled or lapsed one has nothing to cancel. */
const CANCELLABLE: InterviewStatus[] = [
  InterviewStatus.OPEN,
  InterviewStatus.SCHEDULED,
  InterviewStatus.NO_SHOW
];

/**
 * The clock, re-read every CLOCK_TICK_MS so the join button appears when the room opens and the cancel and move
 * buttons go when the interview starts. `clock` is Date.now outside tests.
 */
const useNow = (clock: () => number): number => {
  const [now, setNow] = useState(clock);
  useEffect(() => {
    const id = window.setInterval(() => setNow(clock()), CLOCK_TICK_MS);
    return () => window.clearInterval(id);
  }, [clock]);
  return now;
};

interface Props {
  api: InterviewApi;
  refId: string;
  /** The "Still on?" email's answer: confirm is sent at once (pro-core records it once per appointment), the others open their dialog. */
  initialAnswer?: StillOnAnswer | null;
  /** Called once the initial answer was acted on, so the page can drop it from the URL. */
  onInitialAnswerHandled?: () => void;
  /** What "now" is; tests pass a fixed one. */
  clock?: () => number;
  /**
   * `card` behind the email link and wherever the card stands alone; `section` on the job page of an application, as
   * "Dein Interview" in that page's style: the time, the format and the status as rows, the actions as text buttons.
   */
  layout?: 'card' | 'section';
}

export const InterviewCard = ({
  api,
  refId,
  initialAnswer,
  onInitialAnswerHandled,
  clock = Date.now,
  layout = 'card'
}: Props) => {
  const asSection = layout === 'section';
  const now = useNow(clock);
  const {
    data: card,
    isLoading,
    isIdle,
    error
  } = useInterviewCardQuery(api, refId);

  const hold = useInterviewCommand(api, refId, (startsAt: string) =>
    api.hold(refId, { startsAt })
  );
  const confirmHold = useInterviewCommand(api, refId, () =>
    api.confirmHold(refId)
  );
  const propose = useInterviewCommand(api, refId, (ranges: InterviewTime[]) =>
    api.propose(refId, { ranges })
  );
  const attend = useInterviewCommand(api, refId, () =>
    api.confirmAttendance(refId)
  );
  const move = useInterviewCommand(api, refId, (body: ChangeInterviewRequest) =>
    api.move(refId, body)
  );
  const cancel = useInterviewCommand(
    api,
    refId,
    (body: ChangeInterviewRequest) => api.cancel(refId, body)
  );
  const rebook = useInterviewCommand(api, refId, () => api.rebook(refId));
  const queryClient = useQueryClient();

  const [dialog, setDialog] = useState<InterviewReasonDialogKind | null>(null);
  const [isProposing, setIsProposing] = useState(false);
  const [attended, setAttended] = useState(false);
  const [inCall, setInCall] = useState(false);
  const answerHandledRef = useRef(false);

  // The email's answer, acted on once the card says it still applies. "Ich komme" is safe to send unasked: pro-core
  // records it once per appointment and a repeat changes nothing. Move and cancel need a reason, so they only open.
  useEffect(() => {
    if (!card || !initialAnswer || answerHandledRef.current) return;
    answerHandledRef.current = true;
    const booked =
      card.status === InterviewStatus.SCHEDULED &&
      card.appointment?.status === InterviewAppointmentStatus.CONFIRMED;
    if (initialAnswer === 'confirm' && booked) {
      // The answer stays in the URL until pro-core has it: a failed send can be retried by reloading the link.
      attend.mutate(undefined, {
        onSuccess: () => {
          setAttended(true);
          onInitialAnswerHandled?.();
        }
      });
      return;
    }
    if (
      initialAnswer === 'rebook' &&
      booked &&
      card.canMove &&
      !hasStarted(card, clock())
    ) {
      setDialog('move');
    } else if (
      initialAnswer === 'cancel' &&
      CANCELLABLE.includes(card.status) &&
      !hasStarted(card, clock())
    ) {
      setDialog('cancel');
    }
    onInitialAnswerHandled?.();
  }, [card, initialAnswer, attend, onInitialAnswerHandled, clock]);

  const inSection = (children: ReactNode) => (
    <JobPageSection
      title={getTrans('interviews.card.title')}
      testId="interview-card"
      headingId={SECTION_HEADING_ID}
    >
      {children}
    </JobPageSection>
  );

  if (isLoading || isIdle) {
    const loading = <Skeleton variant="rounded" height={160} />;
    return asSection ? inSection(loading) : loading;
  }

  if (!card) {
    const failure = (
      <Alert severity="error">{getTrans(interviewErrorKey(error))}</Alert>
    );
    return asSection ? inSection(failure) : failure;
  }

  const tz = card.timeZone;
  const busy =
    hold.isLoading ||
    confirmHold.isLoading ||
    propose.isLoading ||
    attend.isLoading ||
    move.isLoading ||
    cancel.isLoading ||
    rebook.isLoading;

  const failed = [hold, confirmHold, propose, attend, rebook].find(
    command => command.isError
  );
  // The rows pro-core refused on the last proposal, and the window to explain them by: the card's own window first,
  // the refusal's as a fallback (#484, #1907). Both empty on an older image, which leaves the form as it was.
  const invalidRows = propose.isError ? invalidProposalsOf(propose.error) : [];
  const proposalWindow = {
    ...(propose.isError ? refusalWindowOf(propose.error) : null),
    ...(card.proposalWindow ?? {})
  };
  const dialogError =
    (dialog === 'move' && move.isError && move.error) ||
    (dialog === 'cancel' && cancel.isError && cancel.error) ||
    null;

  const held =
    card.appointment?.status === InterviewAppointmentStatus.HELD
      ? card.appointment
      : null;
  const booked =
    card.appointment?.status === InterviewAppointmentStatus.CONFIRMED
      ? card.appointment
      : null;

  const started = hasStarted(card, now);
  // pro-core's card says whether the talent already said "Ich komme" for the booked time (#1972), so the confirmed
  // state survives a reload; from then on the card no longer asks, and only "Ich kann nicht" and cancel are left.
  const attendanceConfirmed = !!booked?.attendanceConfirmedAt;

  const openDialog = (kind: InterviewReasonDialogKind) => {
    move.reset();
    cancel.reset();
    setDialog(kind);
  };

  const submitDialog = (body: ChangeInterviewRequest) => {
    const command = dialog === 'move' ? move : cancel;
    command.mutate(body, { onSuccess: () => setDialog(null) });
  };

  // The call happens in the app, over the card; afterwards the card is read again (the interview may have moved on).
  const closeCall = () => {
    setInCall(false);
    void queryClient.invalidateQueries(interviewCardKey(api, refId));
  };

  /** The section's time row: the booked time, a cancelled one struck through, a held one, or none yet. */
  const timeValue = (): ReactNode => {
    if (booked) {
      return (
        <span data-testid="interview-booked-time">
          {formatTimeRange(booked, tz)}
        </span>
      );
    }
    if (card.status === InterviewStatus.CANCELLED && card.appointment) {
      return (
        <s data-testid="interview-cancelled-time">
          {formatTimeRange(card.appointment, tz)}
        </s>
      );
    }
    if (held) return formatTimeRange(held, tz);
    return getTrans('interviews.row.noTime');
  };

  const body = (
    <>
      {failed && (
        <Alert severity="error">
          {failed === propose && invalidRows.length > 0
            ? getTrans('interviews.error.timeOptionsInvalidRows')
            : getTrans(interviewErrorKey(failed.error))}
        </Alert>
      )}
      {attended && (
        <Alert severity="success">{getTrans('interviews.attend.done')}</Alert>
      )}

      {card.status === InterviewStatus.OPEN && (
        <>
          {held && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
              data-testid="interview-hold"
            >
              {/* In the section the time row already names the held time. */}
              {!asSection && (
                <Text variant="subtitle1">{formatTimeRange(held, tz)}</Text>
              )}
              {held.heldUntil && (
                <Text variant="body2" color="text.secondary">
                  {getTransWithParams('interviews.hold.until', {
                    time: formatClock(held.heldUntil, tz)
                  })}
                </Text>
              )}
              <AppButton
                fullWidth={asSection}
                testId="interview-confirm-hold"
                disabled={busy}
                onClick={() => confirmHold.mutate()}
              >
                {getTrans('interviews.hold.confirm')}
              </AppButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Text variant="subtitle2">
              {getTrans(
                held ? 'interviews.pick.other' : 'interviews.pick.title'
              )}
            </Text>
            {card.availableTimes.length === 0 && (
              <Text variant="body2" color="text.secondary">
                {getTrans('interviews.pick.none')}
              </Text>
            )}
            {card.availableTimes
              .filter(time => time.startsAt !== held?.startsAt)
              .map(time => (
                <Box key={time.startsAt} sx={{ display: 'grid', gap: 0.5 }}>
                  <AppButton
                    variant="outlined"
                    fullWidth={asSection}
                    testId="interview-available-time"
                    disabled={busy}
                    onClick={() => hold.mutate(time.startsAt)}
                  >
                    {formatTimeRange(time, tz)}
                  </AppButton>
                  {time.fit && (
                    // PJL: the offered time against the talent's week, as pro-core ranked it.
                    <Text
                      variant="caption"
                      data-testid="interview-time-fit"
                      data-fit={time.fit}
                      color={
                        time.fit === 'OUTSIDE'
                          ? 'text.secondary'
                          : 'success.dark'
                      }
                      sx={{ fontWeight: 600, textAlign: 'center' }}
                    >
                      {getTrans(`interviews.fit.${time.fit}` as TranslationKey)}
                    </Text>
                  )}
                </Box>
              ))}
          </Box>

          {card.proposedTimes.length > 0 && (
            <Box>
              <Text variant="subtitle2">
                {getTrans('interviews.propose.pending')}
              </Text>
              {card.proposedTimes.map(time => (
                <Text key={time.startsAt} variant="body2">
                  {formatTimeRange(time, tz)}
                </Text>
              ))}
            </Box>
          )}

          {isProposing ? (
            <ProposeTimesForm
              timeZone={tz}
              window={proposalWindow}
              invalid={invalidRows}
              isSubmitting={propose.isLoading}
              onSubmit={ranges =>
                propose.mutate(ranges, {
                  onSuccess: () => setIsProposing(false)
                })
              }
            />
          ) : (
            <Action
              variant="text"
              asSection={asSection}
              testId="interview-propose-open"
              onClick={() => setIsProposing(true)}
            >
              {getTrans('interviews.propose.open')}
            </Action>
          )}
        </>
      )}

      {card.status === InterviewStatus.SCHEDULED && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {canJoinNow(card, now) ? (
            <Action
              asSection={asSection}
              testId="interview-join"
              disabled={busy}
              onClick={() => setInCall(true)}
            >
              {getTrans('interviews.join.action')}
            </Action>
          ) : (
            card.joinOpensAt && (
              <Text variant="body2" color="text.secondary">
                {getTransWithParams('interviews.join.opensAt', {
                  time: formatClock(card.joinOpensAt, tz)
                })}
              </Text>
            )
          )}
          {!started && (
            <>
              <Divider />
              {attendanceConfirmed ? (
                <Text variant="subtitle2" data-testid="interview-attending">
                  {getTrans('interviews.attend.confirmed')}
                </Text>
              ) : (
                <>
                  <Text variant="subtitle2">
                    {getTrans('interviews.attend.title')}
                  </Text>
                  <Action
                    asSection={asSection}
                    testId="interview-attend"
                    disabled={busy || attended}
                    onClick={() =>
                      attend.mutate(undefined, {
                        onSuccess: () => setAttended(true)
                      })
                    }
                  >
                    {getTrans('interviews.attend.yes')}
                  </Action>
                </>
              )}
              {card.canMove ? (
                <Action
                  variant="outlined"
                  asSection={asSection}
                  testId="interview-move"
                  disabled={busy}
                  onClick={() => openDialog('move')}
                >
                  {getTrans('interviews.attend.no')}
                </Action>
              ) : (
                <Text variant="body2" color="text.secondary">
                  {getTrans('interviews.move.notPossible')}
                </Text>
              )}
            </>
          )}
        </Box>
      )}

      {card.status === InterviewStatus.NO_SHOW && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Text variant="body2">{getTrans('interviews.noShow.body')}</Text>
          <Action
            asSection={asSection}
            testId="interview-rebook"
            disabled={busy}
            onClick={() => rebook.mutate()}
          >
            {getTrans('interviews.noShow.rebook')}
          </Action>
        </Box>
      )}

      {/* Absagen: pro-core cancels an OPEN, SCHEDULED or NO_SHOW interview; the application stays open. From the
            booked start on it is offered no more: then only joining is left. */}
      {!started && CANCELLABLE.includes(card.status) && (
        <Action
          asSection={asSection}
          variant="text"
          color="error"
          testId="interview-cancel"
          disabled={busy}
          onClick={() => openDialog('cancel')}
        >
          {getTrans('interviews.cancel.action')}
        </Action>
      )}
    </>
  );

  const extras = (
    <>
      {inCall && (
        <Suspense fallback={null}>
          <InterviewCall api={api} card={card} onClose={closeCall} />
        </Suspense>
      )}

      <InterviewReasonDialog
        kind={dialog}
        isSubmitting={move.isLoading || cancel.isLoading}
        errorMessage={
          dialogError ? getTrans(interviewErrorKey(dialogError)) : undefined
        }
        onClose={() => setDialog(null)}
        onConfirm={submitDialog}
      />
    </>
  );

  if (asSection) {
    return inSection(
      <>
        <JobPageRow
          label={getTrans('interviews.row.time')}
          value={timeValue()}
        />
        <JobPageRow
          label={getTrans('interviews.row.format')}
          value={`${getTrans(KIND_COPY[card.kind])} · ${getTransWithParams(
            'interviews.card.duration',
            { count: card.durationMinutes }
          )}`}
        />
        <JobPageRow
          label={getTrans('interviews.row.status')}
          value={getTrans(STATUS_SHORT_COPY[card.status])}
          valueTestId="interview-status"
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {body}
        </Box>
        {extras}
      </>
    );
  }

  return (
    <Card variant="outlined" data-testid="interview-card">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Text variant="h6">{getTrans('interviews.card.title')}</Text>
          <Text variant="body2" color="text.secondary">
            {card.jobOpeningTitle} · {card.companyName}
          </Text>
          <Text variant="body2" color="text.secondary">
            {getTrans(KIND_COPY[card.kind])} ·{' '}
            {getTransWithParams('interviews.card.duration', {
              count: card.durationMinutes
            })}
          </Text>
          <Text variant="body2" data-testid="interview-status">
            {getTrans(STATUS_COPY[card.status])}
          </Text>
        </Box>

        {booked && (
          <Text variant="subtitle1" data-testid="interview-booked-time">
            {formatTimeRange(booked, tz)}
          </Text>
        )}

        {/* pro-core's card carries no appointment once the interview is cancelled; when one comes, it is struck through. */}
        {card.status === InterviewStatus.CANCELLED && card.appointment && (
          <Text
            variant="subtitle1"
            component="s"
            data-testid="interview-cancelled-time"
          >
            {formatTimeRange(card.appointment, tz)}
          </Text>
        )}

        {body}
      </CardContent>
      {extras}
    </Card>
  );
};
